import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useTranslation } from 'react-i18next';
import { useAdminActivityMetrics } from '../../../hooks/useAdminQuery';
import {
  PeopleAlt,
  School,
  Class,
  Person,
  MenuBook,
  BarChart,
  Login,
  Add,
  FileUpload,
  Download,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';
import { ROLES } from '@shared/types';
import StatCard from '../../components/StatCard';
import { CircularProgress } from '@mui/material';

const PrincipalDashboardPage = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const adminStore = useStoreByName('adminStore');
  const userStore = useStoreByName('userStore');

  const { isLoading, isFetching } = useAdminActivityMetrics();
  const isDataLoading = isLoading || isFetching;
  const isRtl = i18n.language === 'he';
  const ArrowIcon = isRtl ? ArrowBack : ArrowForward;

  const userName = userStore.userData?.personalInfo?.name || '';
  const data = adminStore.comprehensiveActivityData;

  const schools = adminStore.availableSchools || [];
  const schoolName = schools.length === 1 ? schools[0].name : schools.map((s: any) => s.name).join(', ');

  const metrics = (() => {
    if (!data) {
      return {
        totalClasses: 0,
        totalTeachers: 0,
        totalStudents: 0,
        activeStudents: 0,
        totalReadings: 0,
        averageReadingsPerStudent: 0,
      };
    }

    if (data.schools?.length > 0) {
      const totalTeachers = data.schools.reduce(
        (sum: number, s: any) => sum + (s.teachers?.length || 0),
        0
      );
      const totalStudents = data.schools.reduce(
        (sum: number, s: any) =>
          sum + (s.classes?.reduce((cs: number, c: any) => cs + (c.students?.length || 0), 0) || 0),
        0
      );
      const totalClasses = data.schools.reduce(
        (sum: number, s: any) => sum + (s.classes?.length || 0),
        0
      );

      return {
        totalClasses,
        totalTeachers,
        totalStudents,
        activeStudents: data.systemActivity?.activeStudents || 0,
        totalReadings: data.systemActivity?.totalReadings || 0,
        averageReadingsPerStudent: data.systemActivity?.averageReadingsPerStudent || 0,
      };
    }

    return {
      totalClasses: data.overview?.totalClasses || 0,
      totalTeachers: data.overview?.totalTeachers || 0,
      totalStudents: data.overview?.totalStudents || 0,
      activeStudents: data.systemActivity?.activeStudents || 0,
      totalReadings: data.systemActivity?.totalReadings || 0,
      averageReadingsPerStudent: data.systemActivity?.averageReadingsPerStudent || 0,
    };
  })();

  const classesData = (() => {
    if (!data?.schools) return [];
    return data.schools.flatMap((school: any) =>
      (school.classes || []).map((cls: any) => ({
        id: cls.id,
        name: cls.name,
        schoolName: school.name,
        teacherName: cls.teacher?.name || null,
        studentCount: cls.students?.length || 0,
      }))
    );
  })();

  return (
    <div className="principal-dashboard" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <div className="principal-dashboard__hero">
        <div className="principal-dashboard__hero-content">
          <h1 className="principal-dashboard__title">
            {t('principalWelcome')}, {userName} 👋
          </h1>
          <div className="principal-dashboard__school-badge">
            <School fontSize="small" />
            <span>{schoolName}</span>
          </div>
          <p className="principal-dashboard__subtitle">
            {t('principalDashboardSubtitle')}
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="principal-dashboard__kpi-section">
        <div className="principal-dashboard__kpi-grid">
          <StatCard
            title={t('classes')}
            value={metrics.totalClasses}
            icon={<Class />}
            variant="orange"
            isLoading={isDataLoading}
          />
          <StatCard
            title={t('teachers')}
            value={metrics.totalTeachers}
            icon={<Person />}
            variant="purple"
            isLoading={isDataLoading}
          />
          <StatCard
            title={t('students')}
            value={metrics.totalStudents}
            icon={<PeopleAlt />}
            variant="primary"
            isLoading={isDataLoading}
          />
          <StatCard
            title={t('activeStudents')}
            value={metrics.activeStudents}
            icon={<Login />}
            variant="cyan"
            isLoading={isDataLoading}
          />
          <StatCard
            title={t('totalReadings')}
            value={metrics.totalReadings}
            icon={<MenuBook />}
            variant="secondary"
            isLoading={isDataLoading}
          />
          <StatCard
            title={t('avgReadingsPerStudent')}
            value={metrics.averageReadingsPerStudent}
            icon={<BarChart />}
            variant="success"
            isLoading={isDataLoading}
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="principal-dashboard__section">
        <h2 className="principal-dashboard__section-title">{t('principalQuickActions')}</h2>
        <div className="principal-dashboard__actions-grid">
          <button
            className="principal-dashboard__action-card principal-dashboard__action-card--purple"
            onClick={() => navigate('/admin-panel/schools-management')}
          >
            <div className="principal-dashboard__action-icon"><Add /></div>
            <span>{t('addClass')}</span>
            <ArrowIcon className="principal-dashboard__action-arrow" fontSize="small" />
          </button>
          <button
            className="principal-dashboard__action-card principal-dashboard__action-card--blue"
            onClick={() => navigate('/admin-panel/users-management')}
          >
            <div className="principal-dashboard__action-icon"><FileUpload /></div>
            <span>{t('importStudents')}</span>
            <ArrowIcon className="principal-dashboard__action-arrow" fontSize="small" />
          </button>
          <button
            className="principal-dashboard__action-card principal-dashboard__action-card--green"
            onClick={() => navigate('/admin-panel/schools-management')}
          >
            <div className="principal-dashboard__action-icon"><Download /></div>
            <span>{t('principalExportData')}</span>
            <ArrowIcon className="principal-dashboard__action-arrow" fontSize="small" />
          </button>
        </div>
      </div>

      {/* Classes Overview */}
      <div className="principal-dashboard__section">
        <h2 className="principal-dashboard__section-title">{t('principalClassesOverview')}</h2>

        {isDataLoading ? (
          <div className="principal-dashboard__loading">
            <CircularProgress size={40} />
          </div>
        ) : classesData.length === 0 ? (
          <div className="principal-dashboard__empty">
            <Class sx={{ fontSize: 48, opacity: 0.3 }} />
            <p>{t('principalNoClasses')}</p>
            <button
              className="principal-dashboard__empty-action"
              onClick={() => navigate('/admin-panel/schools-management')}
            >
              {t('addClass')}
            </button>
          </div>
        ) : (
          <div className="principal-dashboard__classes-grid">
            {classesData.map((cls: any) => (
              <div key={cls.id} className="principal-dashboard__class-card">
                <div className="principal-dashboard__class-header">
                  <h3 className="principal-dashboard__class-name">{cls.name}</h3>
                  <span className="principal-dashboard__class-count">
                    {cls.studentCount} {t('principalStudentsInClass')}
                  </span>
                </div>
                <div className="principal-dashboard__class-teacher">
                  <Person fontSize="small" />
                  <span>
                    {cls.teacherName || t('principalNoTeacher')}
                  </span>
                </div>
                {schools.length > 1 && (
                  <div className="principal-dashboard__class-school">
                    <School fontSize="small" />
                    <span>{cls.schoolName}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation Links */}
      <div className="principal-dashboard__nav-links">
        <button
          className="principal-dashboard__nav-link"
          onClick={() => navigate('/admin-panel/schools-management')}
        >
          <School fontSize="small" />
          {t('principalGoToSchoolManagement')}
          <ArrowIcon fontSize="small" />
        </button>
        <button
          className="principal-dashboard__nav-link"
          onClick={() => navigate('/admin-panel/users-management')}
        >
          <PeopleAlt fontSize="small" />
          {t('principalGoToUsersManagement')}
          <ArrowIcon fontSize="small" />
        </button>
        <button
          className="principal-dashboard__nav-link"
          onClick={() => navigate('/admin-panel/statistics')}
        >
          <BarChart fontSize="small" />
          {t('statistics')}
          <ArrowIcon fontSize="small" />
        </button>
        <button
          className="principal-dashboard__nav-link"
          onClick={() => navigate('/admin-panel/reading-sessions')}
        >
          <MenuBook fontSize="small" />
          {t('readingSessionsAnalytics')}
          <ArrowIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
});

export default PrincipalDashboardPage;
